import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Headset, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EDITOR_ROLES = ['admin', 'cs', 'cro', 'cco', 'super_admin'];
const UNASSIGNED = '__unassigned__';

/**
 * CS Lead assignment for an estimate. Mirrors SalesLeadSelector but lists
 * Customer Success (cs) reps. Persists immediately via PUT
 * /estimates/{id}/cs-lead when an estimateId is present; otherwise bubbles
 * the value up for the create payload (new-estimate wizard).
 */
export const CSLeadSelector = ({ estimateId = null, value = null, onChange, disabled = false }) => {
  const { user } = useAuth();
  const canEdit = !disabled && EDITOR_ROLES.includes(user?.role);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/estimates/assignable-cs-leads`, { withCredentials: true });
        if (!cancelled) setReps(res.data?.cs_leads || []);
      } catch (e) {
        // non-fatal — selector just shows current value
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = useCallback(async (selected) => {
    const userId = selected === UNASSIGNED ? null : selected;
    const rep = userId ? reps.find((r) => r.user_id === userId) : null;
    const newLead = rep ? { user_id: rep.user_id, name: rep.name, email: rep.email } : null;

    if (!estimateId) {
      onChange?.(newLead);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/estimates/${estimateId}/cs-lead`,
        { user_id: userId },
        { withCredentials: true },
      );
      onChange?.(res.data?.cs_lead || null);
      toast.success(newLead ? `CS Lead set to ${newLead.name}` : 'CS Lead cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update CS Lead');
    } finally {
      setSaving(false);
    }
  }, [estimateId, reps, onChange]);

  const currentId = value?.user_id || UNASSIGNED;

  return (
    <Card className="border-slate-200" data-testid="cs-lead-card">
      <CardContent className="p-4">
        <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Headset className="w-3.5 h-3.5 text-[#4ABFCF]" />
          CS Lead
          {(saving || loading) && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </Label>
        <p className="text-[11px] text-slate-400 mb-2 mt-0.5">
          The Customer Success rep assigned to this estimate. They&apos;ll see it in their estimate list. Changes are audited.
        </p>
        <Select value={currentId} onValueChange={handleSelect} disabled={!canEdit || saving || loading}>
          <SelectTrigger className="w-full sm:w-80" data-testid="cs-lead-select">
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent data-testid="cs-lead-select-content">
            <SelectItem value={UNASSIGNED} data-testid="cs-lead-option-unassigned">Unassigned</SelectItem>
            {value?.user_id && !reps.some((r) => r.user_id === value.user_id) && (
              <SelectItem value={value.user_id}>{value.name || value.email}</SelectItem>
            )}
            {reps.map((r) => (
              <SelectItem key={r.user_id} value={r.user_id} data-testid={`cs-lead-option-${r.user_id}`}>
                {r.name}{r.email ? ` · ${r.email}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!canEdit && value?.name && (
          <p className="text-[11px] text-slate-500 mt-1.5" data-testid="cs-lead-readonly">
            Assigned to <span className="font-medium text-[#0F2040]">{value.name}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CSLeadSelector;
